import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Contact, contactApi } from '../../../../config/api';
import { useMailClientStore } from '../../../edgemail-client/store/mailClientStore';
import {
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  PaperClipIcon,
  ShieldCheckIcon,
  ArrowUpRightIcon,
  ArrowDownLeftIcon,
  FunnelIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CommunicationItem {
  id: string;
  type: 'email' | 'call' | 'sms' | 'note';
  date: string;
  subject: string;
  preview: string;
  from: { name: string; address: string };
  to: { name: string; address: string }[];
  direction: 'inbound' | 'outbound';
  confidentiality: 'standard' | 'privileged' | 'work-product';
  source_id: string;
  mailbox_id: string;
  has_attachments: boolean;
  attachment_count: number;
  is_read: boolean;
}

interface CommunicationsResponse {
  communications: CommunicationItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface CommunicationsTabProps {
  contact: Contact;
}

type ChannelFilter = 'all' | 'email' | 'call' | 'note';

// ─── Helpers ────────────────────────────────────────────────────────────────

function groupByDate(items: CommunicationItem[]): Record<string, CommunicationItem[]> {
  const groups: Record<string, CommunicationItem[]> = {};
  for (const item of items) {
    const dateKey = new Date(item.date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(item);
  }
  return groups;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// ─── Component ──────────────────────────────────────────────────────────────

const CommunicationsTab: React.FC<CommunicationsTabProps> = ({ contact }) => {
  const navigate = useNavigate();
  const { setActiveAccountId, selectMessage } = useMailClientStore();
  const [communications, setCommunications] = useState<CommunicationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState<ChannelFilter>('all');

  const clientId = (contact as any).client_id || contact.id;

  const handleOpenEmail = useCallback((item: CommunicationItem) => {
    if (item.type === 'email' && item.mailbox_id) {
      setActiveAccountId(item.mailbox_id);
      selectMessage(item.id);
      navigate('/email');
    }
  }, [navigate, setActiveAccountId, selectMessage]);

  useEffect(() => {
    let cancelled = false;

    async function fetchCommunications() {
      setLoading(true);
      setError(null);
      try {
        const response: CommunicationsResponse = await contactApi.getCommunications(clientId, {
          page,
          limit: 50,
        });
        if (cancelled) return;
        setCommunications(response.communications || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 1);
      } catch (err) {
        if (cancelled) return;
        setError('Failed to load communications');
        setCommunications([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchCommunications();
    return () => { cancelled = true; };
  }, [clientId, page]);

  const filtered = filter === 'all'
    ? communications
    : communications.filter(c => c.type === filter);

  const grouped = groupByDate(filtered);

  // ── Loading skeleton ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-lg" />
        ))}
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 dark:text-red-400">{error}</p>
        <button
          onClick={() => setPage(1)}
          className="mt-4 px-4 py-2 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          Communications
          {total > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
              ({total})
            </span>
          )}
        </h3>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2">
        <FunnelIcon className="w-4 h-4 text-gray-400" />
        {(['all', 'email', 'call', 'note'] as ChannelFilter[]).map(f => (
          <button
            key={f}
            onClick={() => f === 'all' || f === 'email' ? setFilter(f) : undefined}
            disabled={f !== 'all' && f !== 'email'}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              filter === f
                ? 'bg-lex-accent text-lex-accent-contrast'
                : f === 'all' || f === 'email'
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  : 'bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-600 cursor-not-allowed'
            }`}
          >
            {f === 'all' ? 'All' : f === 'email' ? 'Email' : f === 'call' ? 'Calls' : 'Notes'}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-16">
          <EnvelopeIcon className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            No Communications Yet
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Link emails to this contact from EdgeMail to see them here.
            Use the "Link to Contact" button in the email reading pane or compose window.
          </p>
        </div>
      )}

      {/* Chronological feed */}
      {Object.entries(grouped).map(([dateLabel, items]) => (
        <div key={dateLabel}>
          {/* Date separator */}
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {dateLabel}
            </span>
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          </div>

          <div className="space-y-2">
            {items.map(comm => (
              <CommunicationCard key={comm.id} item={comm} onOpen={handleOpenEmail} />
            ))}
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Communication Card ─────────────────────────────────────────────────────

function CommunicationCard({ item, onOpen }: { item: CommunicationItem; onOpen: (item: CommunicationItem) => void }) {
  const DirectionIcon = item.direction === 'outbound' ? ArrowUpRightIcon : ArrowDownLeftIcon;
  const dirLabel = item.direction === 'outbound' ? 'OUT' : 'IN';
  const dirColor = item.direction === 'outbound'
    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30'
    : 'text-lex-accent bg-lex-accent-soft';

  const participants = item.direction === 'outbound'
    ? item.to.map(t => t.name || t.address).join(', ')
    : item.from.name || item.from.address;

  const participantLabel = item.direction === 'outbound' ? 'To' : 'From';

  return (
    <div
      onClick={() => onOpen(item)}
      className={`group flex gap-3 p-4 rounded-lg border transition-colors cursor-pointer ${
        item.is_read
          ? 'bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
          : 'bg-lex-accent-soft border-lex-accent'
      } hover:border-lex-accent`}
    >
      {/* Type icon */}
      <div className="shrink-0 pt-0.5">
        {item.type === 'email' && <EnvelopeIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />}
        {item.type === 'call' && <PhoneIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />}
        {item.type === 'note' && <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {/* Direction badge */}
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded ${dirColor}`}>
            <DirectionIcon className="w-3 h-3" />
            {dirLabel}
          </span>

          {/* Subject */}
          <span className={`text-sm truncate ${item.is_read ? 'text-gray-800 dark:text-gray-200' : 'font-semibold text-gray-900 dark:text-white'}`}>
            {item.subject}
          </span>
        </div>

        {/* Participants */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">
          {participantLabel}: {participants}
        </p>

        {/* Preview */}
        {item.preview && (
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
            {item.preview}
          </p>
        )}

        {/* Bottom row: attachments */}
        {item.has_attachments && (
          <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-400 dark:text-gray-500">
            <PaperClipIcon className="w-3.5 h-3.5" />
            <span>{item.attachment_count} attachment{item.attachment_count !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* Right side: time + badges + open icon */}
      <div className="shrink-0 flex flex-col items-end gap-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatTime(item.date)}
          </span>
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {item.confidentiality !== 'standard' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
            <ShieldCheckIcon className="w-3 h-3" />
            {item.confidentiality === 'privileged' ? 'PRIVILEGED' : 'WORK PRODUCT'}
          </span>
        )}
      </div>
    </div>
  );
}

export default CommunicationsTab;
